import React, { useEffect, useRef, useState } from 'react'

import { isEmpty } from 'lodash'

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Controller, useFormContext } from 'react-hook-form'
import { Info as InfoIcon } from 'react-feather'

import { Card, Label, LoadingBox, Select } from 'components'

import useFetch from 'hooks/useFetch'

import constants from 'constants/index'
import useStyles from './styles'

const MIN_CHARS_TO_SEARCH = 2

// Informações gerais do projeto, compartilhadas pelo app e pelo portal de
// convidados. Os services vêm por prop para o componente não conhecer a
// audiência: quem monta a tela decide entre service.dponet e
// service.guestPortal. O parecer do revisor, o tour e os diálogos ficam na
// página, que muda por audiência.
const PrivacyByDesignForm = ({
  mode = 'new',
  privacyByDesign = {},
  responsibleField = 'user',
  departmentsService,
  usersService,
  quizTemplatesService,
  currentDepartmentName = '',
  currentDepartmentId = null,
  onSubmit = () => {},
  cardId,
}) => {
  const [isSavingInformations, setIsSavingInformations] = useState(false)
  const [departments, setDepartments] = useState([])
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false)
  const [departmentInputValue, setDepartmentInputValue] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const isDepartmentFirstRender = useRef(true)

  const classes = useStyles()

  const isShow = mode === 'show'
  const isEdit = mode === 'edit'
  const isEditOrShow = isEdit || isShow
  // Sem service de departamentos o campo é apenas informativo — é o caso do
  // portal, em que o departamento é o do próprio convidado.
  const hasDepartmentSearch = !!departmentsService && !isEditOrShow
  const hasResponsibleUser = responsibleField === 'user'

  const { NAME_MAX_LENGTH, DESCRIPTION_MAX_LENGTH } = constants.privacyByDesign

  const { control, errors, handleSubmit, setValue, watch } = useFormContext()

  const selectedDepartmentId =
    watch('departmentId') ||
    privacyByDesign?.department?.id ||
    currentDepartmentId

  const handleSubmitForm = async (data) => {
    setIsSavingInformations(true)
    try {
      await onSubmit(data)
    } finally {
      setIsSavingInformations(false)
    }
  }

  useEffect(() => {
    if (!hasDepartmentSearch) return

    if (departmentInputValue.length < MIN_CHARS_TO_SEARCH) {
      setDepartments([])
      return
    }

    const delayRequest = setTimeout(async () => {
      setIsDepartmentsLoading(true)
      try {
        const response = await departmentsService({
          name: departmentInputValue,
          order: 'name ASC',
        })
        setDepartments(response?.data?.departments || [])
      } catch {
        setDepartments([])
      } finally {
        setIsDepartmentsLoading(false)
      }
    }, 500)

    return () => clearTimeout(delayRequest)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentInputValue, hasDepartmentSearch])

  const { response: responseUsers, isLoading: isUsersLoading } = useFetch(
    usersService,
    {
      perPage: 99999,
      order: 'name ASC',
      minimal: true,
      departmentId: selectedDepartmentId,
    },
    [selectedDepartmentId],
    hasResponsibleUser && !!usersService && !!selectedDepartmentId,
  )

  const users = responseUsers?.data?.users || []

  const { response: responseQuizTemplates, isLoading: isQuizTemplatesLoading } =
    useFetch(
      quizTemplatesService,
      { perPage: 99999 },
      [],
      !!quizTemplatesService && !isEditOrShow,
    )

  const quizTemplates =
    responseQuizTemplates?.data?.quizTemplates ||
    responseQuizTemplates?.data?.data ||
    []

  // O questionário é obrigatório na criação, então o campo fica sempre visível:
  // escondê-lo sem opções fazia a validação barrar o submit sem exibir o erro.
  const hasQuizTemplates = !isEmpty(quizTemplates)

  // Trocar o departamento invalida o responsável escolhido para o anterior.
  useEffect(() => {
    if (isDepartmentFirstRender.current) {
      isDepartmentFirstRender.current = false
      return
    }

    if (isEditOrShow || !hasResponsibleUser) return

    setValue('responsibleUserId', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentId])

  return (
    <form id="privacy-by-design-form" onSubmit={handleSubmit(handleSubmitForm)}>
      <Card
        title="Informações Gerais"
        id={cardId}
        actionButtonEnd
        footerContainerProps={{ className: classes.cardFooter, p: 0 }}
        actionButton={
          isEdit && (
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSavingInformations}
              startIcon={
                isSavingInformations && (
                  <CircularProgress size={14} color="inherit" />
                )
              }
            >
              Salvar
            </Button>
          )
        }
      >
        <Grid container spacing={2}>
          <Label
            title="Nome do Projeto *"
            paddingBoxTitle={0}
            xs={12}
            sm={8}
            item
          >
            <Controller
              as={
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Digite o nome do projeto"
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  disabled={isShow}
                />
              }
              control={control}
              name="name"
            />
          </Label>
          <Label
            title={
              <Box display="flex" justifyContent="space-between">
                <Typography variant="subtitle2" color="textPrimary">
                  Departamento do Projeto *
                </Typography>
                {hasDepartmentSearch && (
                  <Tooltip title="Serão exibidos apenas os departamentos associados ao usuário.">
                    <InfoIcon size={14} />
                  </Tooltip>
                )}
              </Box>
            }
            paddingBoxTitle={0}
            xs={12}
            sm={4}
            item
          >
            {hasDepartmentSearch ? (
              <>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => option.name || ''}
                  getOptionSelected={(option, selected) =>
                    option.id === selected.id
                  }
                  value={selectedDepartment}
                  inputValue={departmentInputValue}
                  onInputChange={(_, value) => setDepartmentInputValue(value)}
                  onChange={(_, option) => {
                    setSelectedDepartment(option)
                    setValue('departmentId', option?.id || null)
                  }}
                  loading={isDepartmentsLoading}
                  noOptionsText={
                    departmentInputValue.length < MIN_CHARS_TO_SEARCH
                      ? `Digite no mínimo ${MIN_CHARS_TO_SEARCH} letras para buscar`
                      : 'Nenhum departamento encontrado'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Busque por nome do departamento"
                      variant="outlined"
                      error={!!errors?.departmentId}
                      helperText={errors?.departmentId?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isDepartmentsLoading ? (
                              <CircularProgress color="primary" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  as={<input type="hidden" />}
                  name="departmentId"
                  control={control}
                  defaultValue={null}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={
                    privacyByDesign?.department?.name || currentDepartmentName
                  }
                  disabled
                />
                {!isEditOrShow && (
                  <Controller
                    as={<input type="hidden" />}
                    name="departmentId"
                    control={control}
                    defaultValue={currentDepartmentId}
                  />
                )}
              </>
            )}
          </Label>
          <Label title="Responsável" paddingBoxTitle={0} xs={12} item>
            {!hasResponsibleUser ? (
              <Controller
                as={
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Digite o nome do responsável"
                    inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    error={!!errors.responsibleName}
                    helperText={errors?.responsibleName?.message}
                    disabled={isShow}
                  />
                }
                control={control}
                name="responsibleName"
              />
            ) : isShow ? (
              <TextField
                fullWidth
                variant="outlined"
                value={privacyByDesign?.responsibleUser?.name || ''}
                disabled
              />
            ) : !selectedDepartmentId ? (
              <Select
                items={[]}
                placeholder="Selecione um departamento primeiro"
                value=""
                disabled
              />
            ) : isUsersLoading ? (
              <LoadingBox minHeight={56} />
            ) : (
              <Controller
                as={
                  <Select
                    items={users}
                    placeholder="Selecione um responsável"
                    error={!!errors?.responsibleUserId}
                    helperText={errors?.responsibleUserId?.message}
                  />
                }
                name="responsibleUserId"
                control={control}
              />
            )}
          </Label>
          <Label
            title="Descrição (Finalidade) *"
            paddingBoxTitle={0}
            xs={12}
            item
          >
            <Controller
              as={
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  placeholder="Descreva a finalidade do projeto"
                  inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                  error={!!errors.description}
                  helperText={errors?.description?.message}
                  disabled={isShow}
                />
              }
              control={control}
              name="description"
            />
          </Label>
          <Label
            title="Questionário de Privacy by Design para avaliação *"
            paddingBoxTitle={0}
            xs={12}
            item
          >
            {isEditOrShow ? (
              <TextField
                fullWidth
                variant="outlined"
                value={privacyByDesign?.quizTemplate?.kind || ''}
                disabled
              />
            ) : (
              <Controller
                as={
                  <Select
                    items={quizTemplates}
                    fieldName="kind"
                    placeholder={
                      hasQuizTemplates || isQuizTemplatesLoading
                        ? 'Selecione um questionário'
                        : 'Nenhum questionário disponível'
                    }
                    error={!!errors?.quizTemplateId}
                    helperText={errors?.quizTemplateId?.message}
                    isLoading={isQuizTemplatesLoading}
                  />
                }
                name="quizTemplateId"
                control={control}
                onChange={([event]) => event.target.value}
              />
            )}
          </Label>
        </Grid>
      </Card>
    </form>
  )
}

export default PrivacyByDesignForm
